import React, { useState } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { logo } from "../../assets/images";
const InvoicePopup = ({ selectedItems, onClose }) => {
  const [itemsData, setItemsData] = useState(
    selectedItems.map((item) => ({
      ...item,
      weight: item.weight || "", // Fetch dynamically
      wastage: item.wastage || "", // Fetch dynamically
      gst: "",
      finalPrice: 0,
      oldGoldGram: "",
      oldGoldPrice: "",
      oldGoldTotal: 0,
      todayGoldRate: 0, // Initially set to 0
      billingAddress: "",
      shippingAdderss: "",
    }))
  );

  const [oldGoldExchange, setOldGoldExchange] = useState("No"); // Global state for Old Gold Exchange
  const [oldGoldGram, setOldGoldGram] = useState(""); // Global state for Old Gold Gram
  const [oldGoldPrice, setOldGoldPrice] = useState(""); // Global state for Old Gold Price

  const handleInputChange = (index, field, value) => {
    const updatedItems = [...itemsData];
    if (field === "weight") {
      // Convert weight from grams to milligrams
      value = (parseFloat(value) || 0) * 1000; // Convert to milligrams
    }

    updatedItems[index][field] = value;

    // Update the price when relevant fields change
    recalculateFinalPrice(updatedItems, index);
  };

  const handleOldGoldExchangeChange = (value) => {
    setOldGoldExchange(value);
    recalculateFinalPriceForAllItems(value);
  };

  const handleOldGoldPriceChange = (value) => {
    setOldGoldPrice(value);
    recalculateFinalPriceForAllItems(oldGoldExchange);
  };

  const recalculateFinalPrice = (updatedItems, index) => {
    const weightMg = parseFloat(updatedItems[index].weight) || 0; // In mg
    const wastageMg = parseFloat(updatedItems[index].wastage) || 0; // In mg
    const gst = parseFloat(updatedItems[index].gst) || 0;
    const todayGoldRate = parseFloat(updatedItems[index].todayGoldRate) || 0; // Per gram

    // Convert weight and wastage to grams
    const weightGrams = weightMg / 1000;
    const wastageGrams = wastageMg / 1000;

    let finalPrice = (weightGrams + wastageGrams) * todayGoldRate;
    finalPrice += (gst / 100) * finalPrice; // Apply GST

    // Old Gold Deduction (Global Values)
    let oldGoldTotal = 0;
    if (oldGoldExchange === "Yes") {
      const oldGoldMg = parseFloat(oldGoldGram) || 0; // In mg
      const oldGoldPriceParsed = parseFloat(oldGoldPrice) || 0;
      const oldGoldGrams = oldGoldMg / 1000; // Convert to grams
      oldGoldTotal = oldGoldGrams * oldGoldPriceParsed;

      finalPrice = Math.max(0, finalPrice - oldGoldTotal); // Prevent negative price
    }

    updatedItems[index].oldGoldTotal = oldGoldTotal;
    updatedItems[index].finalPrice = finalPrice;
    setItemsData([...updatedItems]); // Update state
  };

  const recalculateFinalPriceForAllItems = (oldGoldExchangeValue) => {
    const updatedItems = [...itemsData];
    updatedItems.forEach((_, index) => recalculateFinalPrice(updatedItems, index));

    setItemsData(updatedItems); // Update all items with new old gold exchange deduction
  };

  // Calculate total amount
  const totalAmount = itemsData.reduce((acc, item) => acc + item.finalPrice, 0);

  // Calculate total Old Gold Total
  const totalOldGoldTotal = itemsData.reduce((acc, item) => acc + item.oldGoldTotal, 0);

  const generateInvoicePDF = () => {
    const doc = new jsPDF();
  
    // Add Logo
    const img = new Image();
    img.src = logo;
    doc.addImage(img, "PNG", 10, 10, 40, 20);
  
    // Invoice Number
    const now = new Date();
    const invoiceNumber = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}${now
      .getHours()
      .toString()
      .padStart(2, "0")}${now.getMinutes().toString().padStart(2, "0")}`;
  
    // Company Info (Top Right)
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("44, MECHERI, Kukkalpatti", 200, 15, { align: "right" });
    doc.text("Salem, Tamil Nadu, 636453", 200, 20, { align: "right" });
    doc.text("GST: 33CTPPK9946M1ZQ", 200, 25, { align: "right" });
    doc.text("Phone: 04298-278160", 200, 30, { align: "right" });
  
    // Title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE", 105, 45, { align: "center" });
  
    // Invoice Details
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Invoice Number: ${invoiceNumber}`, 10, 55);
    doc.text(`Invoice Date: ${now.toLocaleDateString()}`, 200, 55, {
      align: "right",
    });
  
    // Billing & Shipping
    const billingAddress = itemsData[0]?.billingAddress || "-";
    const shippingAddress = itemsData[0]?.shippingAddress || "-";
  
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Billing Address:", 10, 70);
    doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(billingAddress, 85), 10, 75);
  
    doc.setFont("helvetica", "bold");
    doc.text("Shipping Address:", 120, 70);
    doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(shippingAddress, 85), 120, 75);
  
    // Table Header & Data
    const tableStartY = 95;
    const tableData = itemsData.map((item) => [
      { content: item.productName || "-", styles: { fontStyle: "bold" } },
      { content: `${item.weight || 0} mg`, styles: { fontStyle: "bold" } },
      { content: `${item.wastage || 0} mg`, styles: { fontStyle: "bold" } },
      { content: `${item.gst || 0} %`, styles: { fontStyle: "bold" } },
      {
        content:
          oldGoldExchange === "Yes"
            ? `-${item.oldGoldTotal.toFixed(2)} Rs`
            : "-",
        styles: { fontStyle: "bold" },
      },
      {
        content: `${item.finalPrice?.toFixed(2) || 0} Rs`,
        styles: { fontStyle: "bold" },
      },
    ]);
  
    doc.autoTable({
      startY: tableStartY,
      head: [
        [
          "Product Name",
          "Weight (mg)",
          "Wastage (mg)",
          "GST (%)",
          "Old Gold Deduction",
          "Final Price",
        ],
      ],
      body: tableData,
      styles: { fontSize: 10, halign: "center" },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 10 },
    });
  
    // Totals
    const finalY = doc.lastAutoTable.finalY + 15;
  
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(40, 40, 40);
    doc.text(`Total Old Gold Deduction: Rs. ${totalOldGoldTotal.toFixed(2)}`, 10, finalY);
    doc.text(`Total Amount: Rs. ${totalAmount.toFixed(2)}`, 10, finalY + 10);
  
    // Footer / Notes (optional)
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("Thank you for your business!", 105, 285, { align: "center" });
  
    // Save PDF
    doc.save("invoice.pdf");
  };
  
  
  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-md w-2/3 max-h-screen overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Invoice Details</h3>
  
      
        
  
        {itemsData.map((item, index) => (
          <div key={item._id} className="mb-4 p-4 border rounded-md">
            <h4 className="font-bold">
              {item.productName} - Rs. {item.price}/unit
            </h4>
            <input
              type="number"
              placeholder="Enter Weight (mg)"
              value={item.weight}
              onChange={(e) => handleInputChange(index, "weight", e.target.value)}
              className="border p-2 mb-2 w-full"
            />
            <input
              type="number"
              placeholder="Enter Wastage (mg)"
              value={item.wastage}
              onChange={(e) => handleInputChange(index, "wastage", e.target.value)}
              className="border p-2 mb-2 w-full"
            />
            <input
              type="number"
              placeholder="Enter GST (%)"
              value={item.gst}
              onChange={(e) => handleInputChange(index, "gst", e.target.value)}
              className="border p-2 mb-2 w-full"
            />
            <input
              type="number"
              placeholder="Enter Today's Gold Rate"
              value={item.todayGoldRate}
              onChange={(e) => handleInputChange(index, "todayGoldRate", e.target.value)}
              className="border p-2 mb-2 w-full"
            />
              {/* Billing Address */}

              <input
              type="string"
              placeholder="Enter Billing Address"
              value={item.billingAddress}
              onChange={(e) => handleInputChange(index, "billingAddress", e.target.value)}
              className="border p-2 mb-2 w-full"
            />
              <input
              type="string"
              placeholder="Enter shipping Address"
              value={item.shippingAdderss}
              onChange={(e) => handleInputChange(index, "shippingAdderss", e.target.value)}
              className="border p-2 mb-2 w-full"
            />
              
          </div>
        ))}
  
        {/* Old Gold Exchange Section */}
        <div className="mb-4">
          <label className="block font-semibold">Old Gold Exchange:</label>
          <select
            value={oldGoldExchange}
            onChange={(e) => handleOldGoldExchangeChange(e.target.value)}
            className="border p-2 mb-2 w-full"
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>
  
        {oldGoldExchange === "Yes" && (
          <div>
            <input
              type="number"
              placeholder="Enter Old Gold Weight (mg)"
              value={oldGoldGram}
              onChange={(e) => setOldGoldGram(e.target.value)}
              className="border p-2 mb-2 w-full"
            />
            <input
              type="number"
              placeholder="Enter Old Gold Price Per Gram"
              value={oldGoldPrice}
              onChange={(e) => handleOldGoldPriceChange(e.target.value)}
              className="border p-2 mb-2 w-full"
            />
          </div>
        )}
  
        {/* Totals */}
        <h3 className="text-xl font-bold mt-4">
          Total Old Gold Deduction: Rs. {totalOldGoldTotal.toFixed(2)}
        </h3>
        <h3 className="text-xl font-bold mt-4">Total Amount: Rs. {totalAmount.toFixed(2)}</h3>
  
        {/* Buttons */}
        <button
          onClick={generateInvoicePDF}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto mb-2"
        >
          Generate Invoice PDF
        </button>
  
        <button
          onClick={onClose}
          className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 w-full sm:w-auto"
        >
          Close
        </button>
      </div>
    </div>
  );
  
};

export default InvoicePopup;